/**
 * Cookie Utilities
 *
 * Provides robust cookie extraction functionality with edge case handling.
 * Used primarily for CSRF token extraction from HTTP cookies.
 *
 * @module utils/cookies
 */

/**
 * Check if CSRF debug mode is enabled
 * Enabled in development or when VITE_CSRF_DEBUG is set to 'true'
 */
const CSRF_DEBUG = import.meta.env.DEV || import.meta.env.VITE_CSRF_DEBUG === 'true';

/**
 * Debug logging helper for CSRF operations
 */
function csrfLog(message: string, data?: any) {
  if (CSRF_DEBUG) {
    console.debug(message, data !== undefined ? data : '');
  }
}

/**
 * Error logging helper (always logs, even in production)
 */
function csrfError(message: string, error?: any) {
  console.error(message, error !== undefined ? error : '');
}

/**
 * Extract cookie value by name from document.cookie
 *
 * @description
 * Parses the document.cookie string to extract a specific cookie value.
 * Handles various edge cases including:
 * - Missing cookies (returns null)
 * - URL-encoded values (automatically decoded)
 * - Whitespace around cookie names and values
 * - Empty cookie strings
 *
 * @param name - Cookie name to extract
 * @returns Cookie value or null if not found
 *
 * @example
 * ```typescript
 * // Extract CSRF token from cookie
 * const csrfToken = getCookie('quiz-commerce.x-csrf-token');
 * if (csrfToken) {
 *   // Use token in request header
 *   headers['X-CSRF-Token'] = csrfToken;
 * }
 * ```
 *
 * @example
 * ```typescript
 * // Handle missing cookie gracefully
 * const sessionId = getCookie('session-id');
 * if (!sessionId) {
 *   console.warn('Session cookie not found');
 *   // Handle missing session
 * }
 * ```
 */
export function getCookie(name: string): string | null {
  csrfLog('[CSRF DEBUG] getCookie called for:', name);

  // Handle server-side rendering or environments without document
  if (typeof document === 'undefined') {
    csrfLog('[CSRF DEBUG] document is undefined (SSR context)');
    return null;
  }

  csrfLog('[CSRF DEBUG] document.cookie value:', document.cookie || '(empty)');

  // Handle empty cookie string
  if (!document.cookie) {
    csrfLog('[CSRF DEBUG] document.cookie is empty');
    return null;
  }

  try {
    // Split cookies by semicolon
    const cookies = document.cookie.split(';');

    csrfLog('[CSRF DEBUG] Parsed cookie names:', cookies.map(c => {
      const [cookieName] = c.split('=');
      return cookieName?.trim() || '(empty)';
    }));

    // Find cookie with matching name
    for (const cookie of cookies) {
      // Split by first equals sign
      const [cookieName, ...cookieValueParts] = cookie.split('=');

      // Trim whitespace from cookie name
      const trimmedName = cookieName?.trim();

      // Check if this is the cookie we're looking for
      if (trimmedName === name) {
        // Join back in case value contained '=' characters
        const cookieValue = cookieValueParts.join('=');

        // Trim whitespace and decode URI component
        const trimmedValue = cookieValue?.trim();

        if (!trimmedValue) {
          csrfLog('[CSRF DEBUG] Cookie found but value is empty');
          return null;
        }

        // Decode URI component to handle encoded values
        let decoded: string;
        try {
          decoded = decodeURIComponent(trimmedValue);
          csrfLog('[CSRF DEBUG] Cookie found and decoded successfully:', {
            rawLength: trimmedValue.length,
            decodedLength: decoded.length,
            containsDot: decoded.includes('.'),
            preview: `${decoded.substring(0, 20)}...${decoded.substring(decoded.length - 20)}`
          });
        } catch (decodeError) {
          // If decoding fails, return raw value
          csrfError('[CSRF ERROR] Failed to decode cookie value, using raw value:', decodeError);
          decoded = trimmedValue;
        }

        return decoded;
      }
    }

    // Cookie not found
    csrfLog('[CSRF DEBUG] Cookie not found:', name);
    return null;
  } catch (error) {
    // Always log errors
    csrfError('[CSRF ERROR] Error parsing cookies:', error);
    return null;
  }
}

/**
 * Check if a cookie exists
 *
 * @param name - Cookie name to check
 * @returns true if cookie exists, false otherwise
 *
 * @example
 * ```typescript
 * if (hasCookie('user-preferences')) {
 *   const preferences = getCookie('user-preferences');
 *   // Use preferences
 * }
 * ```
 */
export function hasCookie(name: string): boolean {
  return getCookie(name) !== null;
}
